import { httpsCallable } from 'firebase/functions';
import type {
  BillingStartSubscriptionRequest,
  BillingStartSubscriptionResponse,
} from '@kawai/shared';
import { functions } from '../firebase.js';

const billingStartSubscriptionCallable = httpsCallable<
  BillingStartSubscriptionRequest,
  BillingStartSubscriptionResponse
>(functions, 'billingStartSubscription');

/**
 * TP-22: `billingStartSubscription` Callable の薄いラッパ。
 * Functions 側で caller 検証（saas-admin / tenant-admin）/ initial_paid ゲート /
 * PAY.JP Subscription 作成 + 初月日割り都度課金を行い、失敗時は FunctionsError を throw する。
 */
export async function callBillingStartSubscription(
  input: BillingStartSubscriptionRequest,
): Promise<BillingStartSubscriptionResponse> {
  const res = await billingStartSubscriptionCallable(input);
  return res.data;
}

/**
 * Firebase Functions の Callable エラー（FunctionsError）相当かを duck typing で判定する。
 * `instanceof FunctionsError` は SDK バージョン差・bundle 重複・realm 跨ぎで不安定なため、
 * `code` が `functions/<reason>` 形を満たすかで判定する（tenantCreate.ts と同方針 / S-06b #4）。
 */
interface FunctionsErrorLike {
  code: string;
  message?: string;
}

function isFunctionsErrorLike(err: unknown): err is FunctionsErrorLike {
  return (
    typeof err === 'object' &&
    err !== null &&
    'code' in err &&
    typeof (err as { code: unknown }).code === 'string' &&
    (err as { code: string }).code.startsWith('functions/')
  );
}

/**
 * `billingStartSubscription` のエラーを saas-admin 向け日本語メッセージに変換。
 *
 * - already-exists:     既に月額サブスクが稼働中
 * - failed-precondition: 初期費用未完了 / カード未登録 / 契約停止（サーバ message を併記）
 * - aborted:            開始処理中 or PAY.JP 開始失敗（サーバ message を併記）
 * - not-found:          対象テナント不在
 * - invalid-argument:   入力値検証 NG
 * - permission-denied:  saas-admin 以外
 * - unauthenticated:    セッション切れ
 */
export function billingStartSubscriptionErrorMessage(err: unknown): string {
  if (isFunctionsErrorLike(err)) {
    switch (err.code) {
      case 'functions/already-exists':
        return 'このテナントは既に月額サブスクが稼働しています。';
      case 'functions/failed-precondition':
        return `月額を開始できません：${err.message ?? '前提条件を満たしていません'}`;
      case 'functions/aborted':
        return err.message ?? '処理が混み合っています。少し待って再度お試しください。';
      case 'functions/not-found':
        return '対象のテナントが見つかりません。';
      case 'functions/invalid-argument':
        return `入力値に誤りがあります：${err.message ?? ''}`;
      case 'functions/permission-denied':
        return '権限がありません（saas-admin 権限が必要です）。';
      case 'functions/unauthenticated':
        return 'セッションが切れました。再ログインしてください。';
      default:
        return `月額サブスクの開始に失敗しました（${err.code}）。`;
    }
  }
  if (err instanceof Error) {
    return `月額サブスクの開始に失敗しました：${err.message}`;
  }
  return '月額サブスクの開始に失敗しました。';
}
